import React, { useState, useEffect, useRef } from 'react';
import { Viewer, Entity, ImageryLayer } from 'resium';
import { 
  Cartesian3, 
  Color, 
  Transforms,
  KmlDataSource,
  Ion,
  JulianDate,
  TileMapServiceImageryProvider,
  buildModuleUrl,
  IonImageryProvider,
  OpenStreetMapImageryProvider
} from 'cesium';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Globe, Upload, Trash2, Eye, EyeOff, Layers } from 'lucide-react';
import { toast } from 'sonner';
import 'cesium/Build/Cesium/Widgets/widgets.css';

// Cesium Ion access token from environment variable
Ion.defaultAccessToken = process.env.REACT_APP_CESIUM_ION_TOKEN || '';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const GeographicView = ({ villageData }) => {
  const viewerRef = useRef(null);
  const [kmlLayers, setKmlLayers] = useState([]);
  const [loadedDataSources, setLoadedDataSources] = useState({});
  const [kmlUrl, setKmlUrl] = useState('');
  const [layerVisibility, setLayerVisibility] = useState({});
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [isLoadingSavedLayers, setIsLoadingSavedLayers] = useState(false);

  // Salcette, Goa coordinates
  const salcettePosition = Cartesian3.fromDegrees(73.9712, 15.2993, 100000);

  // Load saved KML layers from backend on mount
  useEffect(() => {
    loadSavedKMLLayers();
  }, []);

  // Load KML layers when viewer is ready
  useEffect(() => {
    if (viewerRef.current && viewerRef.current.cesiumElement && kmlLayers.length > 0) {
      // Re-load any layers that don't have loaded data sources yet
      kmlLayers.forEach(layer => {
        if (!loadedDataSources[layer.id] && !layer.loading) {
          loadKmlIntoViewer(layer.id, layer.source, layer.type);
        }
      });
    }
  }, [viewerRef.current, kmlLayers]);

  const loadSavedKMLLayers = async () => {
    setIsLoadingSavedLayers(true);
    try {
      const response = await fetch(`${API_URL}/api/kml-layers`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        const savedLayers = result.data.map(layer => ({
          ...layer,
          loading: false
        }));
        setKmlLayers(savedLayers);
        
        // Set visibility state
        const visibilityState = {};
        savedLayers.forEach(layer => {
          visibilityState[layer.id] = layer.visible !== false;
        });
        setLayerVisibility(visibilityState);
      }
    } catch (error) {
      console.error('Error loading saved KML layers:', error);
      // Silently fail - don't show error to user on page load
    } finally {
      setIsLoadingSavedLayers(false);
    }
  };

  const saveKMLLayerToBackend = async (layer) => {
    try {
      const response = await fetch(`${API_URL}/api/kml-layers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: layer.name,
          type: layer.type,
          source: layer.source,
          visible: layer.visible
        })
      });
      const result = await response.json();
      
      if (result.success) {
        return result.data.id;
      }
      return null;
    } catch (error) {
      console.error('Error saving KML layer:', error);
      return null;
    }
  };

  const deleteKMLLayerFromBackend = async (layerId) => {
    try {
      await fetch(`${API_URL}/api/kml-layers/${layerId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting KML layer:', error);
    }
  };

  // Color mapping for NRW values
  const getNRWColor = (nrw) => {
    if (nrw < 10) return Color.GREEN;
    if (nrw < 12) return Color.YELLOW;
    if (nrw < 15) return Color.ORANGE;
    return Color.RED;
  };

  // Load KML into Cesium viewer
  const loadKmlIntoViewer = async (layerId, source, type) => {
    if (!viewerRef.current || !viewerRef.current.cesiumElement) return;
    
    const viewer = viewerRef.current.cesiumElement;
    
    try {
      let dataSource;
      
      if (type === 'url') {
        // Load from URL
        dataSource = await KmlDataSource.load(source, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas
        });
      } else if (type === 'file') {
        // Parse KML text and load
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(source, 'text/xml');
        
        // Check for parsing errors
        const parseError = kmlDoc.querySelector('parsererror');
        if (parseError) {
          throw new Error('Invalid KML format');
        }
        
        // Create a blob URL for the KML content
        const blob = new Blob([source], { type: 'application/vnd.google-earth.kml+xml' });
        const blobUrl = URL.createObjectURL(blob);
        
        dataSource = await KmlDataSource.load(blobUrl, {
          camera: viewer.scene.camera,
          canvas: viewer.scene.canvas
        });
        
        // Clean up blob URL
        URL.revokeObjectURL(blobUrl);
      }
      
      if (dataSource) {
        viewer.dataSources.add(dataSource);
        
        // Store reference to data source
        setLoadedDataSources(prev => ({
          ...prev,
          [layerId]: dataSource
        }));
        
        // Fly to the KML data with proper orientation
        viewer.flyTo(dataSource, {
          duration: 2,
          offset: {
            heading: 0.0,
            pitch: -0.5,  // Looking down at 30 degrees
            range: 20000  // 20km altitude for good overview
          }
        });
        
        return dataSource;
      }
    } catch (error) {
      console.error('Error loading KML:', error);
      throw error;
    }
  };

  // Add KML layer from URL
  const handleAddKmlUrl = async () => {
    if (!kmlUrl.trim()) {
      toast.error('Please enter a KML URL');
      return;
    }

    const tempId = Date.now();
    const newLayer = {
      id: tempId,
      name: kmlUrl.split('/').pop() || 'KML Layer',
      source: kmlUrl,
      type: 'url',
      visible: true,
      loading: true
    };

    setKmlLayers([...kmlLayers, newLayer]);
    setLayerVisibility({ ...layerVisibility, [tempId]: true });

    try {
      await loadKmlIntoViewer(tempId, kmlUrl, 'url');
      
      // Save to backend and get permanent ID
      const savedId = await saveKMLLayerToBackend({
        name: newLayer.name,
        type: 'url',
        source: kmlUrl,
        visible: true
      });
      
      // Update layer with saved ID
      setKmlLayers(prev => 
        prev.map(layer => 
          layer.id === tempId ? { ...layer, id: savedId || tempId, loading: false } : layer
        )
      );
      
      if (savedId) {
        // Update visibility state with new ID
        setLayerVisibility(prev => {
          const newVis = { ...prev };
          if (savedId !== tempId) {
            newVis[savedId] = newVis[tempId];
            delete newVis[tempId];
          }
          return newVis;
        });
        
        // Update loaded data sources with new ID
        setLoadedDataSources(prev => {
          const newSources = { ...prev };
          if (savedId !== tempId && prev[tempId]) {
            newSources[savedId] = prev[tempId];
            delete newSources[tempId];
          }
          return newSources;
        });
      }
      
      setKmlUrl('');
      toast.success(`KML layer "${newLayer.name}" loaded and saved!`);
    } catch (error) {
      toast.error(`Failed to load KML layer: ${error.message}`);
      // Remove failed layer
      setKmlLayers(prev => prev.filter(layer => layer.id !== tempId));
      setLayerVisibility(prev => {
        const newVis = { ...prev };
        delete newVis[tempId];
        return newVis;
      });
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.kml') && !file.name.endsWith('.kmz')) {
      toast.error('Please upload a valid KML or KMZ file');
      return;
    }

    const tempId = Date.now();
    const newLayer = {
      id: tempId,
      name: file.name,
      type: 'file',
      visible: true,
      loading: true
    };

    setKmlLayers([...kmlLayers, newLayer]);
    setLayerVisibility({ ...layerVisibility, [tempId]: true });

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const kmlText = e.target.result;
          await loadKmlIntoViewer(tempId, kmlText, 'file');
          
          // Save to backend and get permanent ID
          const savedId = await saveKMLLayerToBackend({
            name: file.name,
            type: 'file',
            source: kmlText,
            visible: true
          });
          
          // Update layer with saved ID and source
          setKmlLayers(prev => 
            prev.map(layer => 
              layer.id === tempId ? { ...layer, id: savedId || tempId, loading: false, source: kmlText } : layer
            )
          );
          
          if (savedId) {
            // Update visibility state with new ID
            setLayerVisibility(prev => {
              const newVis = { ...prev };
              if (savedId !== tempId) {
                newVis[savedId] = newVis[tempId];
                delete newVis[tempId];
              }
              return newVis;
            });
            
            // Update loaded data sources with new ID
            setLoadedDataSources(prev => {
              const newSources = { ...prev };
              if (savedId !== tempId && prev[tempId]) {
                newSources[savedId] = prev[tempId];
                delete newSources[tempId];
              }
              return newSources;
            });
          }
          
          toast.success(`KML file "${file.name}" loaded and saved!`);
        } catch (error) {
          toast.error(`Failed to load KML file: ${error.message}`);
          // Remove failed layer
          setKmlLayers(prev => prev.filter(layer => layer.id !== tempId));
          setLayerVisibility(prev => {
            const newVis = { ...prev };
            delete newVis[tempId];
            return newVis;
          });
        }
      };
      
      reader.onerror = () => {
        toast.error('Failed to read KML file');
        setKmlLayers(prev => prev.filter(layer => layer.id !== tempId));
      };
      
      reader.readAsText(file);
    } catch (error) {
      toast.error('Failed to upload KML file');
      console.error('Error uploading file:', error);
      setKmlLayers(prev => prev.filter(layer => layer.id !== tempId));
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Toggle layer visibility
  const toggleLayerVisibility = (layerId) => {
    const newVisibility = !layerVisibility[layerId];
    setLayerVisibility({
      ...layerVisibility,
      [layerId]: newVisibility
    });
    
    // Show/hide the data source in the viewer
    if (viewerRef.current && loadedDataSources[layerId]) {
      loadedDataSources[layerId].show = newVisibility;
    }
  };

  // Remove layer
  const removeLayer = async (layerId) => {
    // Remove from Cesium viewer
    if (viewerRef.current && viewerRef.current.cesiumElement && loadedDataSources[layerId]) {
      const viewer = viewerRef.current.cesiumElement;
      viewer.dataSources.remove(loadedDataSources[layerId], true);
    }
    
    // Remove from backend
    await deleteKMLLayerFromBackend(layerId);
    
    // Remove from state
    setKmlLayers(kmlLayers.filter(layer => layer.id !== layerId));
    
    const newVisibility = { ...layerVisibility };
    delete newVisibility[layerId];
    setLayerVisibility(newVisibility);
    
    const newDataSources = { ...loadedDataSources };
    delete newDataSources[layerId];
    setLoadedDataSources(newDataSources);
    
    toast.success('Layer removed and deleted from database');
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
          <Globe className="w-8 h-8" />
          Geographic View
        </h1>
        <p className="text-muted-foreground">3D globe visualization with KML layer support for Salcette Block, Goa</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map Container */}
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="p-0">
              <div style={{ height: '600px', position: 'relative' }}>
                <Viewer
                  ref={viewerRef}
                  full
                  timeline={false}
                  animation={false}
                  homeButton={true}
                  sceneModePicker={true}
                  navigationHelpButton={false}
                  baseLayerPicker={true}
                  geocoder={false}
                  style={{ height: '100%', width: '100%' }}
                  scene3DOnly={false}
                  requestRenderMode={false}
                  onReady={(viewer) => {
                    // CRITICAL FIX: Don't remove default imagery, just ensure it's visible
                    // Configure globe FIRST
                    viewer.scene.globe.enableLighting = true;
                    viewer.scene.globe.depthTestAgainstTerrain = true;
                    viewer.scene.globe.show = true;
                    viewer.scene.globe.baseColor = Color.WHITE;
                    
                    // Show sky and atmosphere
                    viewer.scene.skyAtmosphere.show = true;
                    viewer.scene.skyBox.show = true;
                    viewer.scene.sun.show = true;
                    viewer.scene.moon.show = true;
                    
                    // Ensure default imagery is visible
                    if (viewer.imageryLayers.length > 0) {
                      const defaultLayer = viewer.imageryLayers.get(0);
                      defaultLayer.show = true;
                      defaultLayer.alpha = 1.0;
                      console.log('Default Cesium imagery layer enabled');
                    }
                    
                    // Add OpenStreetMap as backup layer (without removing existing)
                    try {
                      const osmProvider = new OpenStreetMapImageryProvider({
                        url: 'https://a.tile.openstreetmap.org/'
                      });
                      const osmLayer = viewer.imageryLayers.addImageryProvider(osmProvider);
                      osmLayer.alpha = 0.5; // Semi-transparent so default shows through
                      console.log('OpenStreetMap backup layer added');
                    } catch (osmError) {
                      console.warn('OpenStreetMap failed to load:', osmError);
                    }
                    
                    console.log('Cesium viewer initialized with', viewer.imageryLayers.length, 'imagery layers');
                    
                    // Wait for globe tiles to load before flying to location
                    const tilesToLoad = viewer.scene.globe.tilesLoaded;
                    if (tilesToLoad) {
                      // Tiles already loaded, fly immediately
                      viewer.camera.flyTo({
                        destination: Cartesian3.fromDegrees(73.9712, 15.2993, 50000),
                        orientation: {
                          heading: 0.0,
                          pitch: -0.7,
                          roll: 0.0
                        },
                        duration: 2
                      });
                    } else {
                      // Listen for tiles to finish loading
                      const removeListener = viewer.scene.globe.tileLoadProgressEvent.addEventListener((queuedTileCount) => {
                        if (queuedTileCount === 0) {
                          // All tiles loaded, now fly to location
                          viewer.camera.flyTo({
                            destination: Cartesian3.fromDegrees(73.9712, 15.2993, 50000),
                            orientation: {
                              heading: 0.0,
                              pitch: -0.7,
                              roll: 0.0
                            },
                            duration: 2
                          });
                          removeListener();
                        }
                      });
                    }
                    
                    // Ensure high-quality rendering
                    viewer.scene.requestRenderMode = false;
                    viewer.scene.maximumRenderTimeChange = Infinity;
                  }}
                >
                  {/* Village Markers */}
                  {Object.entries(villageData).map(([key, village]) => {
                    const position = Cartesian3.fromDegrees(
                      73.9712 + (Math.random() - 0.5) * 0.2,
                      15.2993 + (Math.random() - 0.5) * 0.2,
                      0
                    );

                    return (
                      <Entity
                        key={key}
                        position={position}
                        name={village.name}
                        description={`
                          <div style="font-family: sans-serif;">
                            <h3 style="margin: 0 0 10px 0; color: #333;">${village.name}</h3>
                            <table style="width: 100%; border-collapse: collapse;">
                              <tr>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>WTPs:</strong></td>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;">${village.wtps}</td>
                              </tr>
                              <tr>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>OHTs:</strong></td>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;">${village.ohts}</td>
                              </tr>
                              <tr>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>DMAs:</strong></td>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;">${village.dmas}</td>
                              </tr>
                              <tr>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;"><strong>Households:</strong></td>
                                <td style="padding: 5px; border-bottom: 1px solid #ddd;">${village.households}</td>
                              </tr>
                              <tr>
                                <td style="padding: 5px;"><strong>NRW:</strong></td>
                                <td style="padding: 5px; color: ${village.nrw > 12 ? 'red' : 'green'}; font-weight: bold;">${village.nrw}%</td>
                              </tr>
                            </table>
                          </div>
                        `}
                        point={{
                          pixelSize: 15,
                          color: getNRWColor(village.nrw),
                          outlineColor: Color.WHITE,
                          outlineWidth: 2
                        }}
                        label={{
                          text: village.name,
                          font: '14px sans-serif',
                          fillColor: Color.WHITE,
                          outlineColor: Color.BLACK,
                          outlineWidth: 2,
                          style: 0,
                          pixelOffset: { x: 0, y: -20 },
                          showBackground: true,
                          backgroundColor: Color.fromAlpha(Color.BLACK, 0.7),
                          backgroundPadding: { x: 7, y: 5 }
                        }}
                      />
                    );
                  })}

                  {/* KML Layers are now loaded directly into the viewer via loadKmlIntoViewer() */}
                </Viewer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Layer Controls */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5" />
                KML Layers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add from URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Add KML from URL</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="https://example.com/layer.kml"
                    value={kmlUrl}
                    onChange={(e) => setKmlUrl(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddKmlUrl()}
                  />
                  <Button onClick={handleAddKmlUrl} size="sm">
                    Add
                  </Button>
                </div>
              </div>

              {/* Upload File */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Upload KML File</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".kml,.kmz"
                    onChange={handleFileUpload}
                    className="flex-1 text-sm"
                    id="kml-file-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Select a .kml or .kmz file from your computer
                </p>
              </div>

              {/* Active Layers */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Active Layers ({kmlLayers.length})</label>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {kmlLayers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No KML layers added
                    </p>
                  ) : (
                    kmlLayers.map((layer) => (
                      <div
                        key={layer.id}
                        className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-border"
                      >
                        <button
                          onClick={() => toggleLayerVisibility(layer.id)}
                          className="flex-shrink-0"
                          disabled={layer.loading}
                        >
                          {layer.loading ? (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          ) : layerVisibility[layer.id] ? (
                            <Eye className="w-4 h-4 text-primary" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{layer.name}</p>
                          <div className="flex gap-1 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {layer.type}
                            </Badge>
                            {layer.loading && (
                              <Badge variant="secondary" className="text-xs">
                                Loading...
                              </Badge>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeLayer(layer.id)}
                          className="flex-shrink-0 text-destructive hover:text-destructive/80"
                          disabled={layer.loading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Legend */}
              <div className="space-y-2 pt-4 border-t">
                <label className="text-sm font-medium">NRW Color Legend</label>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-500"></div>
                    <span className="text-xs">Low (&lt; 10%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                    <span className="text-xs">Medium (10-12%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                    <span className="text-xs">High (12-15%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500"></div>
                    <span className="text-xs">Critical (&gt; 15%)</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GeographicView;